/**
 * FL Progress Bar Component
 * Manages progress bar display and automatic gift addition/removal based on cart total
 */
class FlProgressBar extends HTMLElement {
  constructor() {
    super();
    this.steps = [];
    this.cartTotal = 0;
    this.maxAmount = 0;
    this.isProcessing = false;
    this.processedGifts = new Set();
    this.debounceTimer = null;
  }

  connectedCallback() {
    this._parseData();
    this._setupEventListeners();
    this._checkGifts();
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  _parseData() {
    try {
      this.steps = JSON.parse(this.dataset.steps || '[]');
      this.cartTotal = parseInt(this.dataset.cartTotal || '0');
      this.maxAmount = parseInt(this.dataset.maxAmount || '10000');
    } catch (error) {
      console.error('FL Progress Bar: Error parsing data', error);
      this.steps = [];
    }
  }

  _setupEventListeners() {
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    // Listen for cart changes
    document.addEventListener('cart:change', this._onCartChanged.bind(this), { signal });
    document.addEventListener('cart:refresh', this._onCartRefresh.bind(this), { signal });
  }

  async _onCartChanged(event) {
    if (this.isProcessing) return;
    
    const cart = event.detail.cart;
    if (cart) {
      this.cartTotal = cart.total_price;
      this._updateProgressBar();
      
      // Only check gifts if the event is not from our own gift operations
      if (event.detail.baseEvent !== 'gift:add' && 
          event.detail.baseEvent !== 'gift:remove' && 
          event.detail.baseEvent !== 'gift:update') {
        await this._checkGifts(cart);
      }
    }
  }

  async _onCartRefresh() {
    if (this.isProcessing) return;
    
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      this.cartTotal = cart.total_price;
      this._updateProgressBar();
      await this._checkGifts(cart);
    } catch (error) {
      console.error('FL Progress Bar: Error refreshing cart', error);
    }
  }

  _updateProgressBar() {
    // Update progress fill
    const percentage = Math.min((this.cartTotal / this.maxAmount) * 100, 100);
    const fill = this.querySelector('.fl-progress-bar__fill');
    if (fill) {
      fill.style.width = `${percentage}%`;
    }
    
    // Update step states
    this.querySelectorAll('.fl-progress-bar__step').forEach(step => {
      const threshold = parseInt(step.dataset.threshold);
      const achieved = this.cartTotal >= threshold;
      step.dataset.achieved = achieved;
      
      // Add visual feedback
      if (achieved) {
        step.classList.add('achieved');
      } else {
        step.classList.remove('achieved');
      }
    });
  }

  async _checkGifts(cart = null) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      if (!cart) {
        cart = await fetch('/cart.js').then(r => r.json());
      }

      // Get current gift items in cart
      const currentGifts = cart.items.filter(item => 
        item.properties && item.properties._gift === 'true'
      );

      // Check each gift step
      const giftSteps = this.steps.filter(s => s.type === 'gift' && s.product_id);
      
      for (const step of giftSteps) {
        const thresholdCents = parseInt(step.threshold) * 100;
        const shouldHaveGift = this.cartTotal >= thresholdCents;
        
        // Find existing gift for this product (variant ID)
        const existingGift = currentGifts.find(gift => 
          gift.variant_id === parseInt(step.product_id)
        );

        if (shouldHaveGift) {
          // Should have gift - check if we already have exactly 1
          if (!existingGift) {
            // No gift exists, add it
            console.log(`FL Progress Bar: Adding gift variant ${step.product_id} (threshold: ${step.threshold})`);
            await this._addGift(step.product_id);
          } else if (existingGift.quantity !== 1) {
            // Gift exists but wrong quantity, fix it to 1
            console.log(`FL Progress Bar: Fixing gift quantity to 1 for variant ${step.product_id}`);
            await this._updateGiftQuantity(existingGift.key, 1);
          } else {
            // Gift exists with correct quantity (1), do nothing
            console.log(`FL Progress Bar: Gift variant ${step.product_id} already exists with quantity 1`);
          }
        } else {
          // Should not have gift - remove if exists
          if (existingGift) {
            console.log(`FL Progress Bar: Removing gift variant ${step.product_id} (below threshold)`);
            await this._removeGift(existingGift.key);
          }
        }
      }
    } catch (error) {
      console.error('FL Progress Bar: Error checking gifts', error);
    } finally {
      this.isProcessing = false;
    }
  }

  async _addGift(productId) {
    try {
      console.log(`FL Progress Bar: Adding gift product ${productId} to cart`);
      
      // Add gift to cart with _gift property using variant ID directly
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: productId,
          quantity: 1,
          properties: { '_gift': 'true' }
        })
      });

      if (response.ok) {
        const cartData = await response.json();
        console.log(`FL Progress Bar: Successfully added gift product ${productId} to cart`);
        
        // Trigger cart refresh to update UI
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } else {
        const errorData = await response.json();
        console.error('FL Progress Bar: Error adding gift to cart', errorData);
      }
    } catch (error) {
      console.error('FL Progress Bar: Error adding gift', error);
    }
  }

  async _updateGiftQuantity(lineItemKey, quantity) {
    try {
      console.log(`FL Progress Bar: Updating gift item ${lineItemKey} quantity to ${quantity}`);
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: quantity
        })
      });

      if (response.ok) {
        const cartData = await response.json();
        console.log(`FL Progress Bar: Successfully updated gift item ${lineItemKey} quantity to ${quantity}`);
        
        // Trigger cart refresh to update UI
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } else {
        const errorData = await response.json();
        console.error('FL Progress Bar: Error updating gift quantity', errorData);
      }
    } catch (error) {
      console.error('FL Progress Bar: Error updating gift quantity', error);
    }
  }

  async _removeGift(lineItemKey) {
    try {
      console.log(`FL Progress Bar: Removing gift item ${lineItemKey} from cart`);
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: 0
        })
      });

      if (response.ok) {
        const cartData = await response.json();
        console.log(`FL Progress Bar: Successfully removed gift item ${lineItemKey} from cart`);
        
        // Trigger cart refresh to update UI
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } else {
        const errorData = await response.json();
        console.error('FL Progress Bar: Error removing gift from cart', errorData);
      }
    } catch (error) {
      console.error('FL Progress Bar: Error removing gift', error);
    }
  }
}

// Register the custom element
if (!customElements.get('fl-progress-bar')) {
  customElements.define('fl-progress-bar', FlProgressBar);
}
